'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { DialogProps } from '@radix-ui/react-dialog'
import { RefreshCcwIcon } from 'lucide-react'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import z from 'zod'

import { useDeleteNote } from '@/api/hooks/useDeleteNote'
import { useNotes } from '@/api/hooks/useNotes'
import { useSummarizeThread } from '@/api/hooks/useSummarizeThread'
import { useUpdateNote } from '@/api/hooks/useUpdateNote'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIndividuals } from '@/hooks/useIndividuals'
import { useAppSelector } from '@/redux/hooks'

import { CoachPopover } from '../molecules/coach-popover'
import { Field } from '../ui/field'
import { Textarea } from '../ui/textarea'

const schema = z.object({
  note: z.string(),
})

type CreateErrandInput = z.input<typeof schema>

export type CreateThreadSummaryNoteDialogHandle = {
  close(): void
  open(): void
}

type Props = {
  showTrigger?: boolean
  threadId: string | undefined
} & DialogProps

export const CreateThreadSummaryNoteDialog = forwardRef<CreateThreadSummaryNoteDialogHandle, Props>(
  ({ showTrigger = true, threadId, ...dialogProps }, ref) => {
    const [open, setOpen] = useState(false)
    const { selectedIndividualId } = useIndividuals()
    const deleteNote = useDeleteNote()
    const { data: notes = [] } = useNotes(selectedIndividualId)
    const updateNote = useUpdateNote()
    const summarizeThread = useSummarizeThread()
    const isSideChatOpen = useAppSelector(state => state.assistant.isSideChatOpen)
    const { t } = useTranslation()
    const noteExistedBeforeOpen = useRef(false)
    const originalNoteContent = useRef<string | undefined>(undefined)
    const existingNote = useMemo(
      () => notes.find(n => n.relatedToThread === threadId),
      [notes, threadId],
    )
    const form = useForm<CreateErrandInput>({
      defaultValues: {
        note: existingNote?.note,
      },
      resolver: zodResolver(schema),
    })

    useEffect(() => {
      if (open) {
        noteExistedBeforeOpen.current = !!existingNote
        originalNoteContent.current = existingNote?.note
      } else {
        summarizeThread.reset()
      }
    }, [open])

    useEffect(() => {
      const savedNote = summarizeThread.data?.note?.note ?? existingNote?.note
      if (savedNote) form.setValue('note', savedNote)
    }, [existingNote, summarizeThread.data])

    const closeDialog = () => {
      if (summarizeThread.status === 'success' && existingNote) {
        if (noteExistedBeforeOpen.current) {
          // Regeneration: restore original content
          updateNote.mutateAsync({
            individualId: selectedIndividualId!,
            noteId: existingNote.id,
            requestBody: { note: originalNoteContent.current ?? '' },
          })
        } else {
          // First-time summarization: delete the freshly created note
          deleteNote.mutateAsync({ individualId: selectedIndividualId!, noteId: existingNote.id })
        }
      }

      setOpen(false)
      form.reset()
    }

    useImperativeHandle(ref, () => ({
      close: () => setOpen(false),
      open: () => setOpen(true),
    }))

    const handleSubmit = async (values: CreateErrandInput) => {
      if (form.getFieldState('note').isDirty) {
        updateNote.mutateAsync({
          individualId: selectedIndividualId!,
          noteId: existingNote!.id,
          requestBody: { note: values.note },
        })
      }

      form.reset()
      setOpen(false)
    }

    const onClickSaveThreadSummary = () => {
      if (threadId) {
        summarizeThread.mutate({ individualId: selectedIndividualId!, threadId })
      }
    }

    return (
      <AlertDialog onOpenChange={setOpen} open={open} {...dialogProps}>
        {showTrigger &&
          (existingNote ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertDialogTrigger asChild>
                  <Button onClick={onClickSaveThreadSummary} size="icon" variant="outline">
                    <RefreshCcwIcon />
                  </Button>
                </AlertDialogTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t('aiChat.options.regenerateNote')}</TooltipContent>
            </Tooltip>
          ) : (
            <CoachPopover
              childrenAfterAcknowledgement={
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button onClick={onClickSaveThreadSummary}>
                        {t('aiChat.generateNoteButton')}
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{t('aiChat.options.summarize')}</TooltipContent>
                </Tooltip>
              }
              coachKey="summarize-notes"
              description={t('aiChat.generateNoteButton.coachMark.description')}
              disabled={!isSideChatOpen}
              title={t('aiChat.generateNoteButton.coachMark.title')}
            >
              <AlertDialogTrigger asChild>
                <Button onClick={onClickSaveThreadSummary}>{t('aiChat.generateNoteButton')}</Button>
              </AlertDialogTrigger>
            </CoachPopover>
          ))}
        <AlertDialogContent wide>
          <AlertDialogHeader className="space-y-4">
            <AlertDialogTitle>
              {t('dialogs.createUpdateNote.title', { context: existingNote ? 'update' : 'create' })}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary text-base">
              {t('dialogs.summarizeThread.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-[60vh] overflow-auto p-[1px]">
            <FormProvider {...form}>
              <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
                <Field>
                  {summarizeThread.isPending ? (
                    <div className="h-[400px] bg-gradient-to-t from-info/15 dark:from-info/15 to-info/5 flex rounded-md flex-col gap-2 items-center justify-center">
                      <p className="text-lg text-info animate-pulse">
                        {t('dialogs.summarizeThread.generatingSummaryTitle')}
                      </p>
                    </div>
                  ) : (
                    <Textarea
                      className="min-h-[400px]"
                      placeholder={t('dialogs.createUpdateNote.inputPlaceholder')}
                      {...form.register('note')}
                    />
                  )}
                </Field>
              </form>
            </FormProvider>
          </div>
          <AlertDialogFooter>
            <div className="flex justify-end gap-2">
              <Button
                disabled={summarizeThread.isPending}
                onClick={closeDialog}
                type="button"
                variant="outline"
              >
                {t('global.cancel')}
              </Button>
              <Button
                disabled={summarizeThread.isPending}
                onClick={form.handleSubmit(handleSubmit)}
                type="submit"
              >
                {t('global.save')}
              </Button>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  },
)
